import { combineEpics, ofType } from 'redux-observable';
import { switchMap, filter, catchError } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { asyncFinish, asyncError } from '../../../common/+store/index';
import { GET_REPORT, GET_FILTER_FIELDS, GET_STUDENTS_LIST, GET_GROWTH_MATRIX } from './aToF.actions';
import { getAToFAndFlightPlan, getStudentsListApi } from './aToF.api';
import { AssessmentTypes } from '../../../Assessments/+store/assessments/assessments.types';
import { commonApiCallFragment } from '../../../common/+store/utils/utils.api';
import { JobNames } from './aToF.constants';
import { GET_FLIGHT_PLAN } from '../../../FlightPlan/+store/flightPlan';

const isInterim = (assessmentsInfo) => {
  let result = false;
  assessmentsInfo.forEach((a) => {
    if ([AssessmentTypes.Interim, AssessmentTypes.ComboAssessment, AssessmentTypes.DMACInterim].includes(a.type)) {
      result = true;
    }
  });
  return result;
};

const getStudentsListEpic = (action$) =>
  action$.pipe(
    ofType(GET_STUDENTS_LIST),
    switchMap((action) => {
      // D1 (and D3's Academic Achievement, which reuses the d1/staar cells) carry a
      // per-cell version map mirroring the cell path under staarVersionsByCell
      // (e.g. `${uid}/d1/staar/math/doesNotMeet/twoOrMoreRaces` ->
      // `${uid}/d1/staarVersionsByCell/math/doesNotMeet/twoOrMoreRaces`). D2a (and
      // D3's Academic Growth, which reuses the d2a cells) carry a per-subject map at
      // `${uid}/d2aVersionsBySubject/<subject>`, where <subject> is the path segment
      // right after the d2a tree name. Anything else skips the version fetch.
      let versionsPath = null;
      if (action.path.includes('/d1/staar/')) {
        versionsPath = action.path.replace('/d1/staar/', '/d1/staarVersionsByCell/');
      } else {
        const d2aMatch = action.path.match(/^(.*)\/(d2aAnnualGrowth|d2aAcceleratedLearning|d2a)\/([^/]+)\//);
        if (d2aMatch) {
          versionsPath = `${d2aMatch[1]}/d2aVersionsBySubject/${d2aMatch[3]}`;
        }
      }
      return from(
        Promise.all([
          getStudentsListApi(action.orgId, action.path),
          versionsPath ? getStudentsListApi(action.orgId, versionsPath).catch(() => null) : Promise.resolve(null),
        ]).then(([students, versions]) => ({ students, versions }))
      ).pipe(commonApiCallFragment(action$, action, 'Get Students List'));
    }),
    switchMap((result) => {
      if (result.success) {
        return of(
          asyncFinish(result.action.type, {
            students: result.response.students,
            versions: result.response.versions,
            rowLabel: result.action.rowLabel,
            indicatorLabel: result.action.indicatorLabel,
          })
        );
      }
      if (result.actions) return of(...result.actions);
      return of(result.action);
    })
  );

const getReportEpic = (action$) =>
  action$.pipe(
    filter((action) => [GET_REPORT, GET_FLIGHT_PLAN].includes(action.type)),
    switchMap((action) =>
      from(
        getAToFAndFlightPlan({
          schoolYear: action.schoolYear,
          assessmentsInfo: action.assessmentsInfo,
          isInterim: isInterim(action.assessmentsInfo) || false,
          campusDistrictNumber: action.campusDistrictNumber,
          filters: action.filters,
          option: action.option,
          // jobName: JobNames.aToF,
          jobName: action.jobName,
          ignoreCache: action.ignoreCache,
          ignoreTests: action.ignoreTests,
          debug: action.debug,
          returnStudentLists: action.jobName === JobNames.aToF,
        })
      ).pipe(
        commonApiCallFragment(action$, action, 'getAToFAndFlightPlan', {
          action,
        }),
        catchError((error) =>
          of({
            success: false,
            action: asyncError(action.type, 'getAToFAndFlightPlan', error),
          })
        )
      )
    ),
    switchMap((result) => {
      if (result.success) {
        const { jobName } = result.action;
        if (jobName === JobNames.aToF) {
          var returnedData = result && result.response && result.response.data ? result.response.data : null;
          return of(
            asyncFinish(result.action.type, {
              errorMsg: returnedData && returnedData.errorMsg,
              errorName: returnedData && returnedData.errorName,
              jobName: result.action.jobName,
              message: returnedData && returnedData.msg,
              report: returnedData && returnedData.report,
              allCampusReports: returnedData && returnedData.allCampusReports,
              districtOrCampusName: returnedData && returnedData.districtOrCampusName,
              provisionsMessage: returnedData && returnedData.provisionsMessage,
              districtOrCampusNumber: returnedData && returnedData.districtOrCampusNumber,
              schoolYear: returnedData && returnedData.schoolYear,
              reportError: returnedData && returnedData.reportError,
              filterFields: returnedData && returnedData.filterFields,
              originalAction: result.action,
            })
          );
        }
        if (jobName === JobNames.flightPlan) {
          var returnedData = result && result.response && result.response.data ? result.response.data : null;
          return of(
            asyncFinish(result.action.type, {
              errorMsg: returnedData.errorMsg,
              errorName: returnedData.errorName,
              jobName: result.action.jobName,
              flightPlan: returnedData,
              reportError: returnedData.reportError,
              filterFields: returnedData.filterFields,
              originalAction: result.action,
            })
          );
        }
      }
      if (result.actions) return of(...result.actions);
      return of(result.action);
    })
  );

const getFilterFieldsEpic = (action$) =>
  action$.pipe(
    ofType(GET_FILTER_FIELDS),
    switchMap((action) =>
      from(
        getAToFAndFlightPlan({
          schoolYear: action.schoolYear,
          assessmentsInfo: action.assessmentsInfo,
          isInterim: isInterim(action.assessmentsInfo),
          campusDistrictNumber: action.campusDistrictNumber,
          filters: action.filters,
          jobName: JobNames.filterFieldsOnly,
        })
      ).pipe(commonApiCallFragment(action$, action, 'Get Filter Fields'))
    ),
    switchMap((result) => {
      if (result.success) {
        return of(
          asyncFinish(result.action.type, {
            message: result.response.data.msg,
            filterFields: result.response.data.filterFields,
          })
        );
      }
      if (result.actions) return of(...result.actions);
      return of(result.action);
    })
  );

// Same callable, same request as the report on screen, plus the baseline; the
// server answers with just the re-rowed matrix (no report recompute).
const getGrowthMatrixEpic = (action$) =>
  action$.pipe(
    ofType(GET_GROWTH_MATRIX),
    switchMap((action) => {
      const { reportRequest, baseline } = action;
      return from(
        getAToFAndFlightPlan({
          schoolYear: reportRequest.schoolYear,
          assessmentsInfo: reportRequest.assessmentsInfo,
          isInterim: isInterim(reportRequest.assessmentsInfo) || false,
          campusDistrictNumber: reportRequest.campusDistrictNumber,
          filters: reportRequest.filters,
          option: reportRequest.option,
          jobName: JobNames.growthMatrix,
          baseline,
        })
      ).pipe(commonApiCallFragment(action$, action, 'Get Growth Matrix', { action }));
    }),
    switchMap((result) => {
      if (result.success) {
        const returnedData = result && result.response && result.response.data ? result.response.data : null;
        return of(
          asyncFinish(GET_GROWTH_MATRIX, {
            baseline: result.action.baseline,
            growthMatrix: returnedData && returnedData.growthMatrix,
            reportError: returnedData && returnedData.reportError,
          })
        );
      }
      if (result.actions) return of(...result.actions);
      return of(result.action);
    })
  );

const epics = combineEpics(getReportEpic, getFilterFieldsEpic, getStudentsListEpic, getGrowthMatrixEpic);
export default epics;
